import { useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import DataTable from '../../../components/tables/DataTable';
import Modal from '../../../components/modal';
import Pagination from '../../../components/Pagination';
import StatusBadge from '../../../components/StatusBadge';
import {
	listWebhookSubscriptions,
	createWebhookSubscription,
	updateWebhookSubscription,
	removeWebhookSubscription,
	listWebhookEventTypes,
	listWebhookEntityTypes,
} from '../../../utils/queries';

const PAGE_SIZE = 20;
const CHECKBOX_CLASS = 'size-4 rounded border-gray-300 accent-gray-400 cursor-pointer';
const emptyForm = { destinationUrl: '', eventTypes: [] };

export default function WebhooksTab({ storeId }) {
	const [subscriptions, setSubscriptions] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [eventTypeOptions, setEventTypeOptions] = useState([]);
	const [entityTypeOptions, setEntityTypeOptions] = useState([]);

	const refresh = () => {
		if (!storeId) return;
		setLoading(true);
		listWebhookSubscriptions({
			storeId,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
		}).then((result) => {
			if (result?.success) {
				setSubscriptions(result.data.webhook_subscriptions);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId, page, search]);

	useEffect(() => {
		listWebhookEventTypes().then((result) => {
			if (result?.success) setEventTypeOptions(result.data);
		});
		listWebhookEntityTypes().then((result) => {
			if (result?.success) setEntityTypeOptions(result.data);
		});
	}, []);

	const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setError('');
		setOpen(true);
	};

	const openEdit = (sub) => {
		setEditing(sub);
		setForm({
			destinationUrl: sub.destinationUrl,
			eventTypes: toArray(sub.eventTypes),
		});
		setError('');
		setOpen(true);
	};

	const toggleValue = (field, value) => {
		setForm((prev) => ({
			...prev,
			[field]: prev[field].includes(value) ? prev[field].filter((v) => v !== value) : [...prev[field], value],
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		// entityTypes isn't user-facing (see removed Entity Type checkboxes) — every
		// current event type maps to the same set of entities, so just derive it from
		// whichever entity types the store has available rather than asking twice.
		const payload = { ...form, entityTypes: entityTypeOptions.map((option) => option.entityType) };
		const result = editing
			? await updateWebhookSubscription(editing.webhookSubscriptionId, payload)
			: await createWebhookSubscription({ ...payload, storeId });
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setOpen(false);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeWebhookSubscription(id);
		refresh();
	};

	const columns = [
		{
			key: 'destinationUrl',
			header: 'Destination URL',
			render: (value, row) => (
				<button type='button' className='text-black font-medium hover:underline text-left' onClick={() => openEdit(row)}>
					{value}
				</button>
			),
		},
		{ key: 'eventTypes', header: 'Event Types', render: (value) => toArray(value).join(', ') },
		{
			key: 'status',
			header: 'Status',
			render: (value) => <StatusBadge status={value === 'active' ? 'active' : 'inactive'} text={value} />,
		},
		{
			key: 'webhookSubscriptionId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={() => handleRemove(id)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	return (
		<div className='max-w-5xl'>
			<div className='flex items-center justify-between mb-6'>
				<div className='relative max-w-sm'>
					<label>Search (destination URL)</label>
					<div className='relative'>
						<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
						<input
							className='form-input pl-9'
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(0);
							}}
						/>
					</div>
				</div>
				<button type='button' className='btn-primary' disabled={!storeId} onClick={openCreate}>
					New Webhook
				</button>
			</div>

			<DataTable columns={columns} data={subscriptions} loading={loading} emptyHeading='No webhook subscriptions yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={open} setOpen={setOpen} title={editing ? 'Edit Webhook' : 'New Webhook'}>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<div>
						<label>Destination URL</label>
						<input
							className='form-input'
							required
							value={form.destinationUrl}
							onChange={(e) => setForm({ ...form, destinationUrl: e.target.value })}
						/>
					</div>
					<div>
						<label>Event Types</label>
						<div className='space-y-1 mt-1'>
							{eventTypeOptions.map((option) => (
								<label key={option.eventType} className='flex items-center gap-2 font-normal'>
									<input
										type='checkbox'
										className={CHECKBOX_CLASS}
										checked={form.eventTypes.includes(option.eventType)}
										onChange={() => toggleValue('eventTypes', option.eventType)}
									/>
									{option.eventType}
								</label>
							))}
						</div>
					</div>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
